/**
 * Per-user persistence for the customizable Marketing Dashboard overview.
 *
 * Backed by `user_preferences` (migration 101) rather than localStorage so a
 * layout an operator builds on their laptop is still there on the conference
 * room display during the bi-weekly marketing review.
 *
 * The stored value is deliberately validated on read: `user_preferences.value`
 * is free-form JSON, and a tile id that was removed in a later release must
 * degrade to "not pinned" rather than crash the page or render a blank card.
 */

import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/AuthProvider';

/** Namespace for this surface's row in `user_preferences`. */
export const MARKETING_DASHBOARD_PREF_KEY = 'marketing_dashboard';

export interface MarketingDashboardLayout {
  /** Pinned tile ids, in display order. */
  tiles: string[];
}

const QUERY_KEY = ['user-preferences', MARKETING_DASHBOARD_PREF_KEY] as const;

/**
 * Narrows an arbitrary JSON value to a layout, dropping anything unexpected.
 * Returns null (rather than an empty layout) when nothing usable is stored,
 * so callers can distinguish "never customized" from "explicitly cleared".
 */
function parseLayout(value: unknown, knownTileIds: Set<string>): MarketingDashboardLayout | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const raw = (value as { tiles?: unknown }).tiles;
  if (!Array.isArray(raw)) return null;

  const seen = new Set<string>();
  const tiles: string[] = [];
  for (const entry of raw) {
    if (typeof entry !== 'string') continue;
    if (!knownTileIds.has(entry)) continue; // tile retired in a later release
    if (seen.has(entry)) continue;
    seen.add(entry);
    tiles.push(entry);
  }
  return { tiles };
}

/**
 * Reads and writes the current user's overview layout.
 *
 * `knownTileIds` is the catalog the calling page supports; ids outside it are
 * filtered out on read so a stale preference can't produce a phantom tile.
 * `defaultTiles` is used until the user saves something of their own.
 */
export function useMarketingDashboardLayout(
  knownTileIds: readonly string[],
  defaultTiles: readonly string[],
) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const profileId = user?.id ?? null;

  const knownSet = useMemo(() => new Set(knownTileIds), [knownTileIds]);

  const query = useQuery<MarketingDashboardLayout | null>({
    queryKey: [...QUERY_KEY, profileId],
    enabled: !!profileId,
    // Preferences change only when this user edits them, and the mutation
    // below writes straight into the cache — so there's no value in a short
    // stale window here.
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_preferences')
        .select('value')
        .eq('profile_id', profileId!)
        .eq('key', MARKETING_DASHBOARD_PREF_KEY)
        .maybeSingle();
      if (error) throw error;
      return parseLayout(data?.value, knownSet);
    },
  });

  const mutation = useMutation({
    mutationFn: async (layout: MarketingDashboardLayout) => {
      if (!profileId) throw new Error('Not signed in.');
      const { error } = await supabase.from('user_preferences').upsert(
        {
          profile_id: profileId,
          key: MARKETING_DASHBOARD_PREF_KEY,
          value: { tiles: layout.tiles },
        },
        { onConflict: 'profile_id,key' },
      );
      if (error) throw error;
      return layout;
    },
    // Write through to the cache so toggling a tile is instant. The pinned
    // set drives the whole tab's layout, and a roundtrip's worth of lag on
    // every checkbox makes the customizer feel broken.
    onSuccess: (layout) => {
      queryClient.setQueryData([...QUERY_KEY, profileId], layout);
    },
  });

  const tiles = useMemo(() => {
    const stored = query.data?.tiles;
    if (stored && stored.length > 0) return stored;
    return defaultTiles.filter((id) => knownSet.has(id));
  }, [query.data, defaultTiles, knownSet]);

  const setTiles = useCallback(
    (next: readonly string[]) => {
      mutation.mutate({ tiles: next.filter((id) => knownSet.has(id)) });
    },
    [mutation, knownSet],
  );

  const toggleTile = useCallback(
    (id: string) => {
      if (!knownSet.has(id)) return;
      const next = tiles.includes(id) ? tiles.filter((t) => t !== id) : [...tiles, id];
      mutation.mutate({ tiles: next });
    },
    [tiles, mutation, knownSet],
  );

  /** Drops the saved layout back to the built-in default set. */
  const resetToDefault = useCallback(() => {
    mutation.mutate({ tiles: defaultTiles.filter((id) => knownSet.has(id)) });
  }, [mutation, defaultTiles, knownSet]);

  return {
    tiles,
    /** True once the user has saved a layout of their own. */
    isCustomized: (query.data?.tiles?.length ?? 0) > 0,
    isLoading: query.isLoading,
    isSaving: mutation.isPending,
    saveError: mutation.error instanceof Error ? mutation.error.message : null,
    setTiles,
    toggleTile,
    resetToDefault,
  };
}
